import { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { FRANCE_DOM_COUNTRIES, PAYMENT_METHODS, PAYMENT_OPTIONS } from "app/constants";
import { FormattedMessage } from "react-intl";
import Email from "app/pages/.shared/profile/Email";
import Separator from "app/pages/.shared/Separator";
import "./PaymentContact.scss";
import LoadingPlaceholder from "app/pages/.shared/LoadingPlaceholder";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import useOnScreen from "app/pages/.shared/useOnScreen";
import IdentityFieldset from "app/pages/.shared/profile/IdentityFieldset";
import AddressFieldset from "app/pages/.shared/profile/AddressFieldset";
import { useFormikContext } from "formik";
import PhoneIntl from "app/pages/.shared/profile/PhoneIntl";
import FormErrorMessages from "app/pages/.shared/form/FormErrorMessages/FormErrorMessages";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";

const PaymentContact = ({ isLoading, shop, shouldFetchProfile, updateBooking, countryName }) => {
	const {
		setFieldValue,
		setFieldTouched,
		errors = {},
		setFieldError,
		values,
		isSubmitting,
		submitCount,
	} = useFormikContext();
	const { track } = useAnalytics();
	const ref = useRef();
	const isElementOnScreen = useOnScreen({ ref, triggerOnce: false });

	useEffect(() => {
		if (isElementOnScreen && !isLoading) {
			track("payment_view_contact");
		}
	}, [isElementOnScreen, isLoading]);

	useEffect(() => {
		// Si le user a selectionné casino alors que celui-ci n'est pas autorisé, alors on sélectionne le paiement par CB par défauts
		if (
			(FRANCE_DOM_COUNTRIES.indexOf(countryName) === -1 ||
				FRANCE_DOM_COUNTRIES.indexOf(values?.contact["country-name"]) === -1) &&
			values.payment?.type === PAYMENT_METHODS.CASINO
		) {
			setFieldValue("payment.type", PAYMENT_METHODS.CARD);
			setFieldValue("payment.mode", PAYMENT_OPTIONS["1x"]);

			updateBooking({
				paymentType: PAYMENT_METHODS.CARD,
				paymentMode: PAYMENT_OPTIONS["1x"],
			});
		}
	}, [values?.contact["country-name"], countryName]);

	return (
		<div className="payment-contact" ref={ref}>
			{isLoading ? (
				<LoadingPlaceholder
					message={<FormattedMessage id="payment.contact.loading.text" />}
				/>
			) : (
				<>
					<FormErrorMessages
						errors={errors}
						fieldsFilter={["contact"]}
						isSubmitting={isSubmitting}
						submitCount={submitCount}
						message={<FormattedMessage id="error.booking.payment.fields.required" />}
					/>

					<Typography
						variant={TYPOGRAPHY_VARIANTS.LARGE}
						isBold
						className="payment__subtitle"
						component="h3"
					>
						<FormattedMessage id="payment.contact.contact" />
					</Typography>

					<IdentityFieldset isRequired={true} blocPrefixName="contact" />

					<div className="payment__email-phone">
						{shouldFetchProfile ? (
							<Email disabled blocPrefixName="contact" />
						) : (
							<Email blocPrefixName="contact" />
						)}
						<PhoneIntl
							id="phone"
							name="contact.phone"
							shop={shop}
							setFieldValue={setFieldValue}
							setFieldTouched={setFieldTouched}
							setFieldError={setFieldError}
							label={<FormattedMessage id="phone.label" />}
							data-testid="phone-input"
						/>
					</div>

					<Separator design="middle" />

					<Typography
						variant={TYPOGRAPHY_VARIANTS.LARGE}
						isBold
						className="payment__subtitle"
						component="h3"
					>
						<FormattedMessage id="payment.contact.address" />
					</Typography>

					<AddressFieldset
						setFieldValue={setFieldValue}
						shop={shop}
						blocPrefixName="contact"
						setFieldError={setFieldError}
					/>
				</>
			)}
		</div>
	);
};

PaymentContact.propTypes = {
	shop: PropTypes.string,
	shouldFetchProfile: PropTypes.bool,
	isLoading: PropTypes.bool,
	updateBooking: PropTypes.func,
	countryName: PropTypes.string,
};

export default PaymentContact;
